import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import LotteryWeekSelector, { getInitialWeekStart, getWeekRange } from '../../../components/LotteryWeekSelector';
import { PAGE_CARD_SX } from '../../../theme/pageCardSx';
import FilterSelectorWithApp from '../../../components/FilterSelectorWithApp';
import apiClient from '../../../utils/axiosClient';
import type { AttendanceCheckItem } from '../../../types/luckyLottery.types';

dayjs.locale('ko');

// ────────────────────────────────────────
// 상수
// ────────────────────────────────────────

const STORAGE_KEY = 'avatye.ops.lucky-lottery.ticket.received.attendance.filter';

const MAX_DAY = 7;
const DAY_COLORS: Record<number, string> = {
  1: '#93C5FD',
  2: '#60A5FA',
  3: '#3B82F6',
  4: '#2563EB',
  5: '#1D4ED8',
  6: '#1E40AF',
  7: '#1E3A8A',
};

// ────────────────────────────────────────
// 필터 영속화 헬퍼
// ────────────────────────────────────────

type StoredFilter = {
  startDate: string;  // 'YYYY-MM-DD' (주의 일요일)
  endDate: string;    // 'YYYY-MM-DD' (주의 토요일)
  appName: string;
};

const getStoredFilter = (): StoredFilter | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored) as StoredFilter;
  } catch {
    // ignore
  }
  return null;
};

const saveFilter = (filter: StoredFilter) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filter));
  } catch {
    // ignore
  }
};

// ────────────────────────────────────────
// 커스텀 툴팁
// ────────────────────────────────────────

type TooltipPayloadItem = { name: string; value: number; color: string };

function CustomBarTooltip({ active, payload, label }: { active?: boolean; payload?: TooltipPayloadItem[]; label?: string }) {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s, item) => s + (item.value ?? 0), 0);
  return (
    <Box sx={{ bgcolor: '#fff', border: '1px solid #e5e7eb', borderRadius: 1.5, p: 1.5, boxShadow: '0 2px 8px rgba(0,0,0,0.10)', minWidth: 160 }}>
      <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5, color: '#1e293b' }}>{label}</Typography>
      {[...payload].reverse().map((item) => (
        <Box key={item.name} sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.3 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: 2, bgcolor: item.color, flexShrink: 0 }} />
          <Typography sx={{ fontSize: '0.78rem', color: '#374151' }}>
            {item.name}: <strong>{Number(item.value).toLocaleString('ko-KR')}</strong>
          </Typography>
        </Box>
      ))}
      <Box sx={{ borderTop: '1px solid #f1f5f9', mt: 0.5, pt: 0.5 }}>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#1e293b' }}>
          합계: {total.toLocaleString('ko-KR')}
        </Typography>
      </Box>
    </Box>
  );
}

// ────────────────────────────────────────
// 페이지 컴포넌트
// ────────────────────────────────────────

export default function AttendanceCheck() {
  const stored = useMemo(() => getStoredFilter(), []);

  const initialWeek = useMemo(() => {
    if (stored?.startDate && stored?.endDate) {
      return { startDate: stored.startDate, endDate: stored.endDate };
    }
    const sunday = getInitialWeekStart();
    const { start, end } = getWeekRange(sunday);
    return { startDate: start.format('YYYY-MM-DD'), endDate: end.format('YYYY-MM-DD') };
  }, [stored]);

  const [startDate, setStartDate] = useState<string>(initialWeek.startDate);
  const [endDate, setEndDate] = useState<string>(initialWeek.endDate);
  const [selectedApp, setSelectedApp] = useState<string | null>(stored?.appName ?? '앱 전체');
  const [appOptions, setAppOptions] = useState<string[]>(['앱 전체']);
  const [selectedOs, setSelectedOs] = useState<string>('all');
  const [data, setData] = useState<AttendanceCheckItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const fetchAppOptions = useCallback(() => {
    if (!startDate || !endDate) return;
    apiClient.get<string[]>('/api/v1/stat/lucky-lottery/app/list', { params: { start_date: startDate, end_date: endDate } })
      .then((res) => setAppOptions(['앱 전체', ...(res.data || [])]))
      .catch(() => undefined);
  }, [startDate, endDate]);

  const fetchData = useCallback(() => {
    if (!startDate || !endDate) return;
    const app = selectedApp === '앱 전체' ? null : selectedApp;
    saveFilter({ startDate, endDate, appName: selectedApp ?? '앱 전체' });
    setLoading(true);
    apiClient.get<AttendanceCheckItem[]>('/api/v1/analytics/lucky-lottery/ticket/received/attendance', {
      params: {
        start_date: startDate,
        end_date: endDate,
        ...(app ? { app_name: app } : {}),
        ...(selectedOs !== 'all' ? { os_type: selectedOs } : {}),
      },
    })
      .then((res) => { setData(res.data); setErrorMessage(''); })
      .catch(() => { setData(null); setErrorMessage('데이터 로드에 실패했습니다.'); })
      .finally(() => setLoading(false));
  }, [startDate, endDate, selectedApp, selectedOs]);

  useEffect(() => { fetchAppOptions(); }, [fetchAppOptions]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const sortedData = useMemo(
    () => (data ? [...data].sort((a, b) => a.date.localeCompare(b.date)) : []),
    [data],
  );

  // 참여자 수 차트 데이터
  const userChartData = useMemo(() => sortedData.map((item) => {
    const byDay: Record<string, number> = {};
    for (let d = 1; d <= MAX_DAY; d++) {
      const found = item.days.find((e) => e.day === d);
      byDay[`${d}일차`] = found?.userCount ?? 0;
    }
    return { date: dayjs(item.date).format('MM/DD'), ...byDay };
  }), [sortedData]);

  // 응모권 수 차트 데이터
  const ticketChartData = useMemo(() => sortedData.map((item) => {
    const byDay: Record<string, number> = {};
    for (let d = 1; d <= MAX_DAY; d++) {
      const found = item.days.find((e) => e.day === d);
      byDay[`${d}일차`] = found?.ticketSum ?? 0;
    }
    return { date: dayjs(item.date).format('MM/DD'), ...byDay };
  }), [sortedData]);

  // 테이블 데이터: 날짜 내림차순
  const tableRows = useMemo(() => {
    if (!data?.length) return [];
    return [...data].sort((a, b) => b.date.localeCompare(a.date));
  }, [data]);

  const DAYS = Array.from({ length: MAX_DAY }, (_, i) => i + 1);

  return (
    <Box>
      {/* 필터 */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          gap: { xs: 1.5, md: 0 },
        }}
      >
        <Box sx={{ width: { xs: '100%', md: 'auto' } }}>
          <LotteryWeekSelector
            startDate={startDate}
            endDate={endDate}
            onChange={(start, end) => {
              setStartDate(start);
              setEndDate(end);
            }}
          />
        </Box>
        <Box sx={{ ml: { xs: 0, md: 3 }, mt: { xs: 1.5, md: 0 }, width: { xs: '100%', md: 'auto' } }}>
          <FilterSelectorWithApp
            selectedApp={selectedApp}
            appOptions={appOptions}
            onAppChange={setSelectedApp}
            onSearch={fetchData}
            selectedOs={selectedOs}
            onOsChange={setSelectedOs}
          />
        </Box>
      </Box>

      {/* 카드 */}
      <Box sx={PAGE_CARD_SX}>
        {/* 앱 Chip */}
        <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 2, '&::-webkit-scrollbar': { height: 8 }, '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' }, '&::-webkit-scrollbar-thumb': { backgroundColor: '#d1d5db', borderRadius: 4 }, '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#9ca3af' } }}>
          {appOptions.map((name) => (
            <Chip
              key={name}
              label={name}
              onClick={() => setSelectedApp(name)}
              sx={{
                cursor: 'pointer',
                fontWeight: selectedApp === name ? 700 : 400,
                backgroundColor: selectedApp === name ? '#0044D2' : '#fff',
                color: selectedApp === name ? '#fff' : '#374151',
                border: `1px solid ${selectedApp === name ? '#0044D2' : '#e5e7eb'}`,
                '&:hover': { backgroundColor: selectedApp === name ? '#0033a0' : '#f3f4f6' },
                height: 36,
                fontSize: '0.875rem',
              }}
            />
          ))}
        </Stack>

        <Divider sx={{ borderColor: '#f1f5f9', mb: 3 }} />

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400} gap={2}>
            <CircularProgress size={50} sx={{ color: '#0044D2' }} />
            <Typography variant="body1">데이터 로딩 중...</Typography>
          </Box>
        ) : errorMessage ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <Alert severity="error">{errorMessage}</Alert>
          </Box>
        ) : (
          <>
            {/* 바 차트 - 좌우 반반 */}
            <Box sx={{ display: 'flex', gap: 3, mb: 4 }}>
              {/* 참여자 수 */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>참여자 수</Typography>
                  <Tooltip title="일차별 연속 출석체크 참여자 수입니다." arrow placement="right">
                    <InfoOutlinedIcon sx={{ fontSize: 16, color: '#9ca3af', cursor: 'pointer' }} />
                  </Tooltip>
                </Box>
                {userChartData.length === 0 ? (
                  <Box display="flex" justifyContent="center" alignItems="center" height={280}>
                    <Typography color="#94a3b8">데이터가 없습니다.</Typography>
                  </Box>
                ) : (
                  <Box sx={{ height: 280 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={userChartData} margin={{ top: 6, right: 12, left: 0, bottom: 0 }} barCategoryGap="30%">
                        <CartesianGrid strokeDasharray="0" stroke="#f3f4f6" vertical={false} />
                        <XAxis dataKey="date" tick={{ fill: '#6b7280', fontSize: 10 }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => Number(v).toLocaleString('ko-KR')} width={52} />
                        <RechartsTooltip content={<CustomBarTooltip />} cursor={{ fill: '#f1f5f9' }} />
                        <Legend verticalAlign="bottom" iconType="square" iconSize={9} wrapperStyle={{ fontSize: '0.75rem', paddingTop: 6 }} />
                        {DAYS.map((d) => (
                          <Bar key={d} dataKey={`${d}일차`} name={`${d}일차`} stackId="a" fill={DAY_COLORS[d]} radius={d === MAX_DAY ? [3, 3, 0, 0] : undefined} />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                )}
              </Box>

              <Divider orientation="vertical" flexItem sx={{ borderColor: '#f1f5f9' }} />

              {/* 응모권 수 */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>응모권 수</Typography>
                  <Tooltip title="일차별 연속 출석체크 응모권 지급 수입니다." arrow placement="right">
                    <InfoOutlinedIcon sx={{ fontSize: 16, color: '#9ca3af', cursor: 'pointer' }} />
                  </Tooltip>
                </Box>
                {ticketChartData.length === 0 ? (
                  <Box display="flex" justifyContent="center" alignItems="center" height={280}>
                    <Typography color="#94a3b8">데이터가 없습니다.</Typography>
                  </Box>
                ) : (
                  <Box sx={{ height: 280 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={ticketChartData} margin={{ top: 6, right: 12, left: 0, bottom: 0 }} barCategoryGap="30%">
                        <CartesianGrid strokeDasharray="0" stroke="#f3f4f6" vertical={false} />
                        <XAxis dataKey="date" tick={{ fill: '#6b7280', fontSize: 10 }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => Number(v).toLocaleString('ko-KR')} width={52} />
                        <RechartsTooltip content={<CustomBarTooltip />} cursor={{ fill: '#f1f5f9' }} />
                        <Legend verticalAlign="bottom" iconType="square" iconSize={9} wrapperStyle={{ fontSize: '0.75rem', paddingTop: 6 }} />
                        {DAYS.map((d) => (
                          <Bar key={d} dataKey={`${d}일차`} name={`${d}일차`} stackId="b" fill={DAY_COLORS[d]} radius={d === MAX_DAY ? [3, 3, 0, 0] : undefined} />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                )}
              </Box>
            </Box>

            <Divider sx={{ borderColor: '#f1f5f9', mb: 3 }} />

            {/* 테이블 */}
            <Box sx={{ pb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>일별 상세 현황</Typography>
                <Tooltip title="날짜 내림차순으로 정렬됩니다. 합계는 전체 참여자 수 및 응모권 수입니다." arrow placement="right">
                  <InfoOutlinedIcon sx={{ fontSize: 18, color: '#9ca3af', cursor: 'pointer' }} />
                </Tooltip>
              </Box>

              {tableRows.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={200}>
                  <Typography color="#94a3b8">데이터가 없습니다.</Typography>
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0, border: '1px solid #f1f5f9' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {/* 날짜 */}
                        <TableCell rowSpan={2} sx={{ fontWeight: 700, bgcolor: '#f8fafc', borderBottom: '2px solid #e2e8f0', minWidth: 90, fontSize: '0.82rem', color: '#475569', verticalAlign: 'middle' }}>
                          날짜
                        </TableCell>
                        {/* 합계 */}
                        <TableCell colSpan={2} align="center" sx={{ fontWeight: 700, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontSize: '0.82rem', color: '#475569', borderLeft: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                          합계
                        </TableCell>
                        {/* 1~7일차 */}
                        {DAYS.map((d) => (
                          <TableCell key={d} colSpan={2} align="center" sx={{ fontWeight: 700, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontSize: '0.82rem', color: DAY_COLORS[d], borderLeft: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                            {d}일차
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        {/* 합계 서브헤더 */}
                        {(['참여자 수', '응모권 수'] as const).map((sub) => (
                          <TableCell key={`합계-${sub}`} align="right" sx={{ fontWeight: 600, bgcolor: '#f8fafc', borderBottom: '2px solid #e2e8f0', fontSize: '0.75rem', color: '#64748b', py: 0.5, px: 1, borderLeft: sub === '참여자 수' ? '1px solid #e2e8f0' : 'none', whiteSpace: 'nowrap' }}>
                            {sub}
                          </TableCell>
                        ))}
                        {/* 일차별 서브헤더 */}
                        {DAYS.map((d) => (
                          (['참여자 수', '응모권 수'] as const).map((sub) => (
                            <TableCell key={`${d}일차-${sub}`} align="right" sx={{ fontWeight: 600, bgcolor: '#f8fafc', borderBottom: '2px solid #e2e8f0', fontSize: '0.75rem', color: '#64748b', py: 0.5, px: 1, borderLeft: sub === '참여자 수' ? '1px solid #e2e8f0' : 'none', whiteSpace: 'nowrap' }}>
                              {sub}
                            </TableCell>
                          ))
                        ))}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {tableRows.map((row, rowIdx) => (
                        <TableRow key={row.date} hover sx={{ '&:hover': { bgcolor: '#f8fafc' }, bgcolor: rowIdx % 2 === 0 ? '#fff' : '#fafafa' }}>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.82rem', color: '#1e293b', whiteSpace: 'nowrap' }}>
                            {row.date}
                          </TableCell>
                          {/* 합계 */}
                          <TableCell align="right" sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#1e293b', borderLeft: '1px solid #f1f5f9', px: 1 }}>
                            {row.totalUserCount.toLocaleString('ko-KR')}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#1e293b', px: 1 }}>
                            {row.totalTicketSum.toLocaleString('ko-KR')}
                          </TableCell>
                          {/* 일차별 */}
                          {DAYS.map((d) => {
                            const entry = row.days.find((e) => e.day === d);
                            return (
                              <>
                                <TableCell key={`${d}-user`} align="right" sx={{ fontSize: '0.82rem', color: '#374151', borderLeft: '1px solid #f1f5f9', px: 1 }}>
                                  {entry ? entry.userCount.toLocaleString('ko-KR') : '-'}
                                </TableCell>
                                <TableCell key={`${d}-ticket`} align="right" sx={{ fontSize: '0.82rem', color: '#374151', px: 1 }}>
                                  {entry ? entry.ticketSum.toLocaleString('ko-KR') : '-'}
                                </TableCell>
                              </>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
